import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmt, fmtPct } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Line, ReferenceLine, XAxis, YAxis, Tooltip, Bar } from "recharts";

const RANGES = [
  { k: "1mo", i: "1d", l: "1M" },
  { k: "3mo", i: "1d", l: "3M" },
  { k: "6mo", i: "1d", l: "6M" },
  { k: "1y", i: "1d", l: "1Y" },
  { k: "5y", i: "1wk", l: "5Y" },
];

export default function Chart() {
  const [sp] = useSearchParams();
  const [symbol, setSymbol] = useState((sp.get("s") || "SPY").toUpperCase());
  const [rng, setRng] = useState(RANGES[1]);
  const [data, setData] = useState(null);
  const [quote, setQuote] = useState(null);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async (sym = symbol, r = rng) => {
    setLoading(true); setAi(null);
    try {
      const [ch, q] = await Promise.all([
        api.get(`/market/chart/${sym}`, { params: { period: r.k, interval: r.i } }),
        api.get(`/market/quote/${sym}`),
      ]);
      setData(ch.data); setQuote(q.data);
    } catch (e) {
      toast.error(`Chart load failed: ${e.response?.data?.detail || sym}`);
      setData(null); setQuote(null);
    } finally { setLoading(false); }
  };

  const runAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/technical-analysis", { symbol, timeframe: rng.k });
      setAi(data);
      toast.success(`AI TA complete · ${data.trend}`);
    } catch (e) {
      toast.error("AI analysis failed");
    } finally { setAiLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.candles.map((c, i) => ({
      time: c.time.slice(5, 10),
      close: c.close,
      high: c.high,
      low: c.low,
      volume: c.volume,
      ema20: data.ema20?.[i],
      up: c.close >= c.open,
    }));
  }, [data]);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="chart-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-4">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ AI TECHNICAL ANALYSIS</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Live Chart Intelligence</h1>
      </div>

      {/* Controls */}
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3 mb-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="chart-symbol-input"
          value={symbol}
          onChange={e => setSymbol(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === "Enter" && load()}
          className="bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2 text-sm font-mono-data text-white w-40 focus:outline-none focus:border-[color:var(--border-focus)]"
        />
        <button data-testid="chart-load-btn" onClick={() => load()} className="px-3 py-2 text-xs font-mono-data border border-[color:var(--border-default)] hover:bg-[color:var(--bg-hover)] transition-colors">LOAD</button>
        <div className="flex gap-0 border border-[color:var(--border-default)]">
          {RANGES.map(r => (
            <button
              key={r.k}
              data-testid={`range-${r.l}`}
              onClick={() => { setRng(r); load(symbol, r); }}
              className={`px-3 py-1.5 text-[11px] font-mono-data ${rng.k === r.k ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"}`}
            >{r.l}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          {quote && (
            <>
              <div className="font-mono-data text-lg text-white" data-testid="chart-price">{fmt(quote.price)}</div>
              <div className={`font-mono-data text-sm ${quote.change_pct >= 0 ? "text-bull" : "text-bear"}`}>
                {quote.change_pct >= 0 ? "▲" : "▼"} {fmtPct(quote.change_pct)}
              </div>
            </>
          )}
          <button data-testid="run-ai-ta-btn" onClick={runAI} disabled={aiLoading || !data} className="bg-white text-black px-4 py-2 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors">
            {aiLoading ? "ANALYZING…" : "◆ RUN AI TA"}
          </button>
        </div>
      </div>

      {/* Chart panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4">
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-3" style={{ height: 520 }} data-testid="chart-panel">
          {loading || !data ? (
            <div className="h-full flex items-center justify-center text-xs font-mono-data text-[color:var(--text-muted)]">
              {loading ? "Loading market data…" : "No data"}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="time" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
                <YAxis yAxisId="price" domain={["dataMin", "dataMax"]} tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} orientation="right" />
                <YAxis yAxisId="vol" orientation="left" hide domain={[0, "dataMax"]} />
                <Tooltip
                  contentStyle={{ background: "#121212", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }}
                  labelStyle={{ color: "#A1A1AA" }}
                  itemStyle={{ color: "#fff" }}
                />
                <Bar yAxisId="vol" dataKey="volume" fill="#27272A" opacity={0.4} />
                <Line yAxisId="price" type="monotone" dataKey="close" stroke="#FFFFFF" strokeWidth={1.5} dot={false} name="Close" />
                <Line yAxisId="price" type="monotone" dataKey="ema20" stroke="#0A84FF" strokeWidth={1} dot={false} strokeDasharray="3 3" name="EMA20" />
                {data.support && <ReferenceLine yAxisId="price" y={data.support} stroke="#32D74B" strokeDasharray="4 4" label={{ value: `S ${data.support}`, fill: "#32D74B", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideBottomLeft" }} />}
                {data.resistance && <ReferenceLine yAxisId="price" y={data.resistance} stroke="#FF3B30" strokeDasharray="4 4" label={{ value: `R ${data.resistance}`, fill: "#FF3B30", fontSize: 10, fontFamily: "JetBrains Mono", position: "insideTopLeft" }} />}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Side panel */}
        <div className="space-y-3">
          {quote && (
            <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-4" data-testid="quote-panel">
              <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-2">SNAPSHOT</div>
              <div className="font-serif-elite text-xl mb-3">{quote.long_name || quote.symbol}</div>
              <SideRow k="PRICE" v={fmt(quote.price)} />
              <SideRow k="CHANGE" v={fmtPct(quote.change_pct)} tone={quote.change_pct >= 0 ? "bull" : "bear"} />
              <SideRow k="HIGH" v={fmt(quote.high)} />
              <SideRow k="LOW" v={fmt(quote.low)} />
              <SideRow k="VOLUME" v={fmt(quote.volume, 0)} />
              {quote.market_cap ? <SideRow k="MKT CAP" v={fmt(quote.market_cap)} /> : null}
              {quote.pe ? <SideRow k="P/E" v={fmt(quote.pe)} /> : null}
              {quote.sector ? <SideRow k="SECTOR" v={quote.sector} mono={false} /> : null}
              {data && <>
                <SideRow k="SUPPORT" v={fmt(data.support)} tone="bull" />
                <SideRow k="RESISTANCE" v={fmt(data.resistance)} tone="bear" />
              </>}
            </div>
          )}

          {ai && <AiPanel ai={ai} />}
          {!ai && !aiLoading && data && (
            <div className="border border-dashed border-[color:var(--border-default)] p-4 text-center text-xs font-mono-data text-[color:var(--text-muted)]">
              Click <span className="text-white">◆ RUN AI TA</span> for AI-driven trend, momentum, and pattern read.
            </div>
          )}
          {aiLoading && (
            <div className="border border-[color:var(--border-default)] p-4 text-xs font-mono-data text-[color:var(--text-muted)]" data-testid="ai-loading">
              AI Technical Agent analyzing…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SideRow({ k, v, tone, mono = true }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-white";
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[color:var(--border-default)] last:border-0">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{k}</span>
      <span className={`${mono ? "font-mono-data" : "font-body"} text-sm ${c}`}>{v}</span>
    </div>
  );
}

function AiPanel({ ai }) {
  const trendColor = ai.trend?.includes("UP") ? "text-bull" : ai.trend?.includes("DOWN") ? "text-bear" : "text-[color:var(--text-secondary)]";
  return (
    <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-4" data-testid="ai-ta-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono-data text-gold tracking-widest">◆ AI TECHNICAL READ</div>
        <div className="text-[10px] font-mono-data text-gold">{ai.confidence_pct}%</div>
      </div>
      <div className={`font-serif-elite text-xl ${trendColor}`}>{ai.trend}</div>
      <p className="text-xs text-white font-body leading-relaxed mt-2">{ai.actionable_note}</p>
      <div className="mt-3 space-y-2">
        <MiniBlock label="MOMENTUM" v={ai.momentum} />
        <MiniBlock label="STRUCTURE" v={ai.structure} />
        <MiniBlock label="PATTERN" v={ai.pattern_detected} />
        <MiniBlock label="VOLUME" v={ai.volume_signal} />
        <MiniBlock label="NEXT MOVE" v={ai.next_probable_move} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] font-mono-data tracking-widest text-bull mb-1">SUPPORT ZONES</div>
          {(ai.key_support || []).map((s) => <div key={`sup-${s}`} className="font-mono-data text-sm text-white">{fmt(s)}</div>)}
        </div>
        <div>
          <div className="text-[9px] font-mono-data tracking-widest text-bear mb-1">RESISTANCE ZONES</div>
          {(ai.key_resistance || []).map((s) => <div key={`res-${s}`} className="font-mono-data text-sm text-white">{fmt(s)}</div>)}
        </div>
      </div>
    </div>
  );
}

function MiniBlock({ label, v }) {
  return (
    <div>
      <div className="text-[9px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className="text-xs text-white font-body">{v}</div>
    </div>
  );
}
